import type { IntlShape } from 'react-intl'
import { PaymentProductBillingBasis } from '../enum'
import {
	LoanFinancingType,
	LoanInterestDaysMonth,
	LoanInterestDaysYear,
	LoanStatus,
} from '@viilu-web/shared/enums/loans'
import type { CreateLoanInput } from './types'
import { loanFinancingTypeMessage } from './messages'
import { getPaymentProductBillingBasisOptions } from '../PaymentProducts/utils'
import type { LoanCreateFormInputs } from './LoanCreateDialog/validation'

export const createLoanDefaultValues: LoanCreateFormInputs = {
	name: '',
	companyUUID: '',
	companyName: '',
	financingType: LoanFinancingType.CAPITAL,
	startDateEstimated: '',
	endDateEstimated: '',
	originalLoanAmount: 0,
	sharesPayable: false,
	sharePaymentDates: [],
	usableOnBuildingManagerCertificate: true,
	interestDaysMonth: LoanInterestDaysMonth.THIRTY_DAYS,
	interestDaysYear: LoanInterestDaysYear.THREE_SIXTY,
	referenceNumber: '',
	status: LoanStatus.DRAFT,
	startDate: '',
	endDate: '',
	currency: 'EUR',
	type: '',
	grantedBy: '',
	duration: '',
	amountDrawn: 0,
	dateDrawn: '',
	drawn: false,
	interestType: '',
	startingInterestPercentage: 0,
	interestFirstDueDate: '',
	interestInspectionDates: [],
	marginalPercentage: 0,
	amortizationAmount: 0,
	amortizationFirstDueDate: '',
	amortizationDates: [],
	startingUnits: 0,
	billingBasis: PaymentProductBillingBasis.AREA,
	shareholders: [],
	selectableSharegroups: [],
}

export const loanFinancingTypeInput: CreateLoanInput = {
	title: (intl) =>
		intl.formatMessage({
			defaultMessage: 'Määritä lainan tyyppi',
			description: 'Define the financing type of the loan',
		}),
	key: 'financingType',
	show: true,
	type: 'radio',
	menuItems: (intl) => {
		return Object.values(LoanFinancingType).map((financingType) => ({
			label: loanFinancingTypeMessage(intl, financingType),
			value: financingType,
		}))
	},
}

export const billingBasisInput: CreateLoanInput = {
	title: (intl) =>
		intl.formatMessage({
			defaultMessage: 'Jakoperuste',
			description: 'Billing basis',
		}),
	key: 'billingBasis',
	show: true,
	type: 'select',
	menuItems: (intl) => getPaymentProductBillingBasisOptions(intl),
}

export const STEPS_FOR_CAPITAL_LOAN = (intl: IntlShape) => [
	{
		label: intl.formatMessage({
			defaultMessage: 'Lainan tyyppi',
			description: 'Title for form step: loan type',
		}),
		id: 'step1',
	},
	{
		label: intl.formatMessage({
			defaultMessage: 'Yleiset tiedot',
			description: 'Title for form step: basic information',
		}),
		id: 'step2',
	},
	{
		label: intl.formatMessage({
			defaultMessage: 'Lisätiedot',
			description: 'Title for form step: advanced information',
		}),
		id: 'step3',
	},
	{
		label: intl.formatMessage({
			defaultMessage: 'Lainaosuudet',
			description: 'Title for form step: loan shares',
		}),
		id: 'step4',
	},
	{
		label: intl.formatMessage({
			defaultMessage: 'Sopimukset ja maksulaji',
			description: 'Title for form step: loan payment product',
		}),
		id: 'step5',
	},
]

export const STEPS_FOR_NON_CAPITAL_LOAN = (intl: IntlShape) =>
	STEPS_FOR_CAPITAL_LOAN(intl).slice(0, -2)
